// PostHog error tracking, a pilot running alongside Sentry from 2026-10-29
// (project "Forge (forge.gluska.co)", 696642, Digital Mundo LLC org). Errors
// only: no pageviews, autocapture, session recording, surveys, flags or
// person profiles. Sentry (main.tsx) is unchanged and stays the alerting
// system of record until the 2026-11-12 decision (Forge task #4082).
//
// beforeSend lets only $exception events out, rebuilt from an allow-list of
// properties. Forge screens carry client data, prompts and task text, so
// exception messages are scrubbed of emails, phone and long numbers, tokens
// and bearer headers; stack frames keep file, function and position only;
// every URL loses its query string and hash, and the /shared and /preview
// token in a path is redacted.

export const POSTHOG_TOKEN = "phc_sMU4YpcZRyCDLR2N4wHwCQqAS4kAeyMQrnW69GA2BaLU"
export const POSTHOG_API_HOST = "https://us.i.posthog.com"
/** Production only: local dev and Pages previews report nothing. */
export const POSTHOG_HOSTNAMES = ["forge.gluska.co"]

const REDACTED = "[redacted]"
const PATTERNS = [
  /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, // email addresses
  /\(?\b\d{3}\)?[-. ]\d{3}[-. ]\d{4}\b/g, // US phone numbers
  /\b\d{9,19}\b/g, // card, account and other long numbers
  /\b[A-Za-z0-9_-]{32,}\b/g, // API, share and preview tokens
  /\bBearer\s+[A-Za-z0-9._~+/-]+=*/gi,
]

export function scrub(value: string): string {
  return PATTERNS.reduce((s, re) => s.replace(re, REDACTED), value)
}

/** Path segments after these hold a bearer-like token (App.tsx routes). */
const TOKEN_ROUTES = new Set(["shared", "preview"])

function scrubPath(pathname: string): string {
  const parts = pathname.split("/")
  for (let i = 1; i < parts.length; i++) {
    if (TOKEN_ROUTES.has(parts[i - 1]) && parts[i]) parts[i] = REDACTED
  }
  return scrub(parts.join("/"))
}

export function scrubUrl(url: string): string {
  try {
    const u = new URL(url)
    return u.origin + scrubPath(u.pathname)
  } catch {
    return scrubPath(url.split(/[?#]/)[0])
  }
}

// Network blips, aborted requests, browser quirks and the stale route chunks
// main.tsx and RouteErrorBoundary already recover from: not bugs.
export const IGNORED_ERRORS = [
  "Failed to fetch",
  "Load failed",
  "NetworkError when attempting to fetch resource.",
  "AbortError",
  "The operation was aborted.",
  "The user aborted a request.",
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications.",
  "Failed to fetch dynamically imported module",
  "Unable to preload CSS",
  "Importing a module script failed",
]

/** Event properties that survive; anything else posthog-js adds is dropped. */
const PROPERTY_KEYS = new Set([
  "token", "distinct_id", "$process_person_profile", "$geoip_disable",
  "$exception_list", "$exception_level", "$exception_fingerprint",
  "$exception_message", "$exception_type", "$exception_values", "$exception_source", "$exception_handled",
  "$lib", "$lib_version", "$browser", "$browser_version", "$os", "$os_version", "$device_type",
  "$current_url", "$host", "$pathname", "$session_id", "$window_id", "$insert_id", "$time",
  "environment", "source",
])

/** Stack frame keys that survive: no source lines or variables. */
const FRAME_KEYS = new Set(["filename", "abs_path", "function", "lineno", "colno", "in_app", "platform", "module", "chunk_id"])

type ExceptionEntry = {
  type?: unknown
  value?: unknown
  mechanism?: unknown
  stacktrace?: { type?: unknown; frames?: Record<string, unknown>[] } | null
}

function messageOf(entry: ExceptionEntry): string {
  return `${typeof entry.type === "string" ? entry.type : ""}: ${typeof entry.value === "string" ? entry.value : ""}`
}

// posthog-js's CaptureResult, structurally (this file stays importable without the SDK).
type Event = { event: string; properties: Record<string, unknown> }

/** posthog-js `before_send`. Returns null to drop the event. */
export function beforeSend<T extends Event>(event: T | null): T | null {
  if (!event || event.event !== "$exception") return null
  const source = event.properties ?? {}
  const list = Array.isArray(source.$exception_list) ? (source.$exception_list as ExceptionEntry[]) : []
  const messages = list.map(messageOf)
  if (typeof source.$exception_message === "string") messages.push(source.$exception_message)
  if (messages.some((m) => IGNORED_ERRORS.some((ignored) => m.includes(ignored)))) return null

  const properties: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(source)) {
    if (PROPERTY_KEYS.has(key)) properties[key] = value
  }
  properties.$exception_list = list.map((entry) => ({
    ...entry,
    type: typeof entry.type === "string" ? scrub(entry.type) : entry.type,
    value: typeof entry.value === "string" ? scrub(entry.value) : entry.value,
    stacktrace: entry.stacktrace
      ? {
          ...entry.stacktrace,
          frames: (entry.stacktrace.frames ?? []).map((frame) => {
            const kept: Record<string, unknown> = {}
            for (const [key, value] of Object.entries(frame)) {
              if (FRAME_KEYS.has(key)) kept[key] = typeof value === "string" && /^https?:\/\//i.test(value) ? scrubUrl(value) : value
            }
            return kept
          }),
        }
      : entry.stacktrace,
  }))
  // Older posthog-js versions flattened the first exception into these.
  for (const key of ["$exception_message", "$exception_type"]) {
    if (typeof properties[key] === "string") properties[key] = scrub(properties[key] as string)
  }
  if (Array.isArray(properties.$exception_values)) {
    properties.$exception_values = properties.$exception_values.map((v) => (typeof v === "string" ? scrub(v) : v))
  }
  if (typeof properties.$current_url === "string") properties.$current_url = scrubUrl(properties.$current_url)
  if (typeof properties.$pathname === "string") properties.$pathname = scrubPath(properties.$pathname)
  properties.$process_person_profile = false
  properties.$geoip_disable = true

  const out = { event: event.event, properties } as Record<string, unknown>
  for (const key of ["uuid", "timestamp"]) {
    if (key in event) out[key] = (event as Record<string, unknown>)[key]
  }
  return out as T
}
